import React, { useEffect, useState, useCallback, useRef } from 'react';
import './Styles/HomePage.css';
import '../constants/styleConstants.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { LOCAL_STORAGE_KEYS } from '../constants/Constants';
import axiosClient from '../services/axios_client';
import Header from '../Components/Header';
import LoadingSpinner from '../Components/LoadingSpinner';
import VideoCard from '../Components/VideoCard';
import BottomNav from '../Components/BottomNav';

// Types
type Category = { id: string | number; name: string; };
type Language = { id: string | number; name: string; };
// FINAL, FINAL, FINAL FIX: The Video type now correctly uses `likes_count` and `comments_count`
type Video = {
  id: string | number;
  title: string;
  description: string | null;
  category: Category | null;
  language: Language | null;
  thumbnail_path: string | null;
  likes: number;
  comments: number;
};
type Paged<T> = { data: T[]; current_page: number; last_page: number; };

const PER_PAGE = 12;

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const observer = useRef<IntersectionObserver | null>(null);
  const [isLoggedIn] = useState(() => localStorage.getItem(LOCAL_STORAGE_KEYS.isLoggedIn) === 'true');

  const [videos, setVideos] = useState<Video[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [categories, setCategories] = useState<Category[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [catRes, langRes] = await Promise.all([
          axiosClient.get<Category[]>('/video-categories'),
          axiosClient.get<Language[]>('/languages-list')
        ]);
        setCategories(catRes.data);
        setLanguages(langRes.data);
      } catch (e) { console.error("Could not load filters", e); }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const fetchVideos = async () => {
      const params = new URLSearchParams({ page: '1', perPage: String(PER_PAGE) });
      if (submittedSearch) params.append('search', submittedSearch);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedLanguage) params.append('language', selectedLanguage);
      try {
        const { data } = await axiosClient.get<Paged<Video>>(`/videos?${params.toString()}`);
        setVideos(data.data);
        setPage(2);
        setHasMore(data.current_page < data.last_page);
      } catch (e) {
        setError('Could not load videos. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [submittedSearch, selectedCategory, selectedLanguage]);

  const lastVideoElementRef = useCallback((node: Element | null) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        const params = new URLSearchParams({ page: String(page), perPage: String(PER_PAGE) });
        if (submittedSearch) params.append('search', submittedSearch);
        if (selectedCategory) params.append('category', selectedCategory);
        if (selectedLanguage) params.append('language', selectedLanguage);
        axiosClient.get<Paged<Video>>(`/videos?${params.toString()}`)
          .then(({ data }) => {
            setVideos((prev) => [...prev, ...data.data]);
            setPage((prev) => prev + 1);
            setHasMore(data.current_page < data.last_page);
          });
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, page, submittedSearch, selectedCategory, selectedLanguage]);

  const handleVideoClick = (video: Video) => {
    const destination = `/video/${video.id}`;
    if (isLoggedIn) {
      navigate(destination);
    } else {
      localStorage.setItem(LOCAL_STORAGE_KEYS.intendedVideoPath, destination);
      navigate('/login');
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedSearch(search);
  };

  return (
    <div className="home-page">
      <Header />
      <main className="content">
        <section className="hero">
          <h1 className="headline">Welcome to Hameruha Learning</h1>
          <p className="subhead">Your journey to knowledge begins here. Explore our diverse library of high-quality educational videos tailored for you.</p>
        </section>

        <section className="filters">
          <form className="search-form" onSubmit={handleSearchSubmit}>
            <input type="text" className="search-input" placeholder="Search for videos..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <button type="submit" className="search-icon-btn" aria-label="Search">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            </button>
          </form>
          <div className="filter-group">
            <div className="select">
              <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <svg className="select-caret" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
            <div className="select">
              <select value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)}>
                <option value="">All Languages</option>
                {languages.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <svg className="select-caret" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
            </div>
          </div>
        </section>

        <section className="video-grid" aria-live="polite">
          {videos.map((v, index) => (
            <VideoCard key={`${v.id}-${index}`} video={v} onClick={handleVideoClick} lastRef={videos.length === index + 1 ? lastVideoElementRef : undefined} />
          ))}
        </section>

        {loading && <div className="loading-container"><LoadingSpinner /></div>}
        {error && <div className="panel error" role="alert">{error}</div>}
        {!loading && videos.length === 0 && !error && <div className="empty">No results found. Try a different search or filter.</div>}
      </main>
      <BottomNav />
    </div>
  );
};

export default HomePage;